import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../lib/CartContext";
import { useAuth } from "../lib/AuthContext";
import { useI18n } from "../lib/LanguageContext";
import { formatIDR, localizeContent } from "../lib/translations";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Trash2, Plus, Minus, ArrowLeft, CreditCard, Smartphone, QrCode, Wallet, Building2, PackageCheck } from "lucide-react";
import VoucherInput from "../components/VoucherInput";
import { useMidtrans } from "../lib/useMidtrans";

const METHODS = [
  { v: "qris", key: "qris", icon: QrCode },
  { v: "gopay", key: "gopay", icon: Wallet },
  { v: "shopeepay", key: "shopeepay", icon: Smartphone },
  { v: "card", key: "card", icon: CreditCard },
  { v: "bank_transfer", key: "bank_transfer", icon: Building2 },
  { v: "cod", key: "cod", icon: PackageCheck },
];

export default function CheckoutPage() {
  const { items, subtotal, setQty, remove, clear } = useCart();
  const { user } = useAuth(); const { t, lang } = useI18n(); const nav = useNavigate();
  const { midtrans, paySnap } = useMidtrans();
  const [voucher, setVoucher] = useState(null);
  const [form, setForm] = useState({
    customer_name: user?.full_name || "", phone: "", email: user?.email || "",
    address: "", city: "", postal_code: "", province: "", country: "Indonesia",
    payment_method: "qris", notes: "",
  });
  const [placed, setPlaced] = useState(null); const [loading, setLoading] = useState(false);
  const shipping = subtotal >= 500000 ? 0 : (items.length ? 25000 : 0);
  const discount = voucher ? Math.min(voucher.discount, subtotal) : 0;
  const total = Math.max(0, subtotal + shipping - discount);
  const paymentMethodLabel = (method) => t(`checkout_extra.methods.${method}`);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) return toast.error(t("checkout_extra.signin_checkout"));
    if (items.length === 0) return toast.error(t("products.empty_cart"));
    setLoading(true);
    try {
      const payload = { ...form, items: items.map(i => ({ product_id: i.id, quantity: i.qty })), voucher_code: voucher?.code };
      const r = await api.post("/orders", payload);
      setPlaced(r.data); clear();
      toast.success(t("checkout.order_success"));
      // Auto-open Midtrans Snap for real e-wallet payment when configured
      if (midtrans.configured && ["qris","gopay","shopeepay"].includes(r.data.payment_method)) {
        try {
          await paySnap({
            order_id: r.data.id,
            onSuccess: () => { toast.success(t("checkout_extra.payment_success")); nav("/produk"); },
            onPending: () => toast.info(t("checkout_extra.pending")),
            onError: () => toast.error(t("checkout_extra.payment_failed")),
          });
        } catch (e) { toast.error(e.message || "Midtrans error"); }
      }
    } catch (err) { toast.error(err.response?.data?.detail || t("checkout_extra.order_failed")); }
    finally { setLoading(false); }
  };

  const confirmPayment = async () => {
    try { await api.post(`/orders/${placed.id}/confirm`, {}); toast.success(t("payment.payment_success")); nav("/produk"); }
    catch { toast.error(t("checkout_extra.confirm_failed")); }
  };

  if (placed) return (
    <div className="max-w-2xl mx-auto px-6 py-16" data-testid="order-placed">
      <Card className="rounded-2xl p-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-teal-soft flex items-center justify-center"><PackageCheck className="w-6 h-6 text-teal"/></div>
          <div>
            <h1 className="font-display text-2xl">{t("checkout_extra.order_created")}</h1>
            <p className="text-xs text-muted-foreground">{t("checkout_extra.order_id")}: <span className="font-mono">{placed.id}</span></p>
          </div>
        </div>
        <div className="border-t border-teal/10 pt-4 space-y-2 text-sm">
          <Row l={t("checkout_extra.name")} v={placed.customer_name}/>
          <Row l={t("checkout_extra.phone")} v={placed.phone}/>
          <Row l={t("checkout_extra.address")} v={`${placed.address}, ${placed.city}, ${placed.province} ${placed.postal_code}, ${placed.country}`}/>
          <Row l={t("checkout_extra.total")} v={formatIDR(placed.total)} bold/>
          <Row l={t("checkout_extra.method")} v={paymentMethodLabel(placed.payment_method)}/>
        </div>
        {placed.status === "pending_payment" && placed.payment_method !== "cod" && (
          <div className="mt-6 p-4 rounded-lg bg-amber-50 border border-amber-200 text-sm">
            <div className="font-semibold text-amber-800 mb-2">⚠ {t("checkout_extra.simulation")}</div>
            <p className="text-xs text-amber-800 mb-3">{t("checkout_extra.simulation_desc")}</p>
            <Button onClick={confirmPayment} className="rounded-full bg-teal hover:bg-teal/90 text-white" data-testid="confirm-payment-btn">{t("checkout_extra.simulate_success")}</Button>
          </div>
        )}
        <Link to="/produk"><Button variant="outline" className="mt-6 rounded-full"><ArrowLeft className="w-4 h-4 mr-1"/> {t("checkout_extra.back_products")}</Button></Link>
      </Card>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-6 py-12" data-testid="checkout-page">
      <Link to="/produk" className="text-teal text-sm flex items-center mb-6 hover:underline"><ArrowLeft className="w-4 h-4 mr-1"/> {t("common.back")}</Link>
      <h1 className="font-display text-3xl md:text-4xl font-medium mb-8">{t("checkout.title")}</h1>
      <div className="grid lg:grid-cols-3 gap-6">
        <form onSubmit={submit} className="lg:col-span-2 space-y-6">
          <Card className="rounded-2xl p-6">
            <h2 className="font-display text-lg mb-4">1. {t("checkout.title").split("&")[0]}</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label={t("checkout.name")}><Input required value={form.customer_name} onChange={(e)=>setForm({...form, customer_name: e.target.value})} data-testid="co-name"/></Field>
              <Field label={t("checkout.phone")}><Input required type="tel" placeholder="0812xxxxxxxx" value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})} data-testid="co-phone"/></Field>
              <Field label={t("checkout.email")}><Input type="email" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} data-testid="co-email"/></Field>
              <div/>
              <div className="md:col-span-2"><Label className="text-xs">{t("checkout.address")}</Label>
                <textarea required value={form.address} onChange={(e)=>setForm({...form, address: e.target.value})} className="w-full mt-1 border border-input rounded-md p-2 text-sm h-20" placeholder="Jl. Contoh No. 12 RT 04 RW 02" data-testid="co-address"/>
              </div>
              <Field label={t("checkout.city")}><Input required value={form.city} onChange={(e)=>setForm({...form, city: e.target.value})} data-testid="co-city"/></Field>
              <Field label={t("checkout.province")}><Input required value={form.province} onChange={(e)=>setForm({...form, province: e.target.value})} data-testid="co-province"/></Field>
              <Field label={t("checkout.postal")}><Input required value={form.postal_code} onChange={(e)=>setForm({...form, postal_code: e.target.value})} data-testid="co-postal"/></Field>
              <Field label={t("checkout.country")}><Input value={form.country} onChange={(e)=>setForm({...form, country: e.target.value})}/></Field>
              <div className="md:col-span-2"><Label className="text-xs">{t("checkout.notes")}</Label>
                <textarea value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})} className="w-full mt-1 border border-input rounded-md p-2 text-sm h-16"/>
              </div>
            </div>
          </Card>

          <Card className="rounded-2xl p-6">
            <h2 className="font-display text-lg mb-4">2. {t("checkout.payment")}</h2>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
              {METHODS.map(m => {
                const Icon = m.icon; const active = form.payment_method === m.v;
                return (
                  <label key={m.v} className={`border rounded-lg p-3 cursor-pointer flex items-center gap-3 ${active ? "border-teal bg-teal-soft" : "border-teal/15 hover:border-teal/30"}`} data-testid={`pm-${m.v}`}>
                    <input type="radio" name="pm" value={m.v} checked={active} onChange={()=>setForm({...form, payment_method: m.v})} className="sr-only"/>
                    <Icon className={`w-5 h-5 ${active ? "text-teal" : "text-muted-foreground"}`}/>
                    <span className="text-sm font-medium">{paymentMethodLabel(m.key)}</span>
                  </label>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground mt-3 italic">{t("checkout_extra.payment_note")}</p>
          </Card>

          <Button type="submit" disabled={loading || items.length===0} className="w-full rounded-full bg-teal hover:bg-teal/90 text-white h-11" data-testid="place-order-btn">{loading ? t("checkout.processing") : t("checkout.place_order")}</Button>
        </form>

        <Card className="rounded-2xl p-6 h-fit sticky top-24" data-testid="cart-summary">
          <h3 className="font-display text-lg mb-4">{t("products.cart")}</h3>
          {items.length === 0 ? <p className="text-sm text-muted-foreground">{t("products.empty_cart")}</p> :
            <div className="space-y-3">
              {items.map(i => (
                <div key={i.id} className="flex items-center gap-3" data-testid={`cart-item-${i.id}`}>
                  <img src={i.image} alt={localizeContent(i.name, lang)} className="w-14 h-14 rounded-md object-cover"/>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{localizeContent(i.name, lang)}</div>
                    <div className="text-xs text-muted-foreground">{formatIDR(i.price)}</div>
                    <div className="flex items-center gap-1 mt-1">
                      <button onClick={()=>setQty(i.id, i.qty - 1)} className="w-6 h-6 rounded border border-teal/20 flex items-center justify-center"><Minus className="w-3 h-3"/></button>
                      <span className="w-8 text-center text-sm" data-testid={`qty-${i.id}`}>{i.qty}</span>
                      <button onClick={()=>setQty(i.id, i.qty + 1)} className="w-6 h-6 rounded border border-teal/20 flex items-center justify-center"><Plus className="w-3 h-3"/></button>
                      <button onClick={()=>remove(i.id)} className="ml-2 text-destructive"><Trash2 className="w-3.5 h-3.5"/></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          }
          <div className="border-t border-teal/10 mt-4 pt-4 space-y-2">
            <VoucherInput amount={subtotal} scope="order" onApply={setVoucher}/>
            <div className="pt-2 space-y-1 text-sm">
              <Row l={t("products.subtotal")} v={formatIDR(subtotal)}/>
              <Row l={t("products.shipping")} v={shipping === 0 ? t("checkout_extra.free") : formatIDR(shipping)}/>
              {discount > 0 && <Row l={`Voucher ${voucher.code}`} v={`− ${formatIDR(discount)}`} teal/>}
              <Row l={t("products.total")} v={formatIDR(total)} bold/>
              {shipping === 0 && subtotal > 0 && <p className="text-[10px] text-teal">🎉 {t("checkout.free_shipping")}</p>}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

function Field({ label, children }) { return <div><Label className="text-xs">{label}</Label><div className="mt-1">{children}</div></div>; }
function Row({ l, v, bold, teal }) { return <div className={`flex justify-between ${bold?"font-display text-base font-medium":""} ${teal?"text-teal":""}`}><span className={teal?"text-teal":"text-muted-foreground"}>{l}</span><span>{v}</span></div>; }
